import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { Alert, Button, Layout, Steps, Typography } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { UNKNOWN_ERROR_MESSAGE } from "../constants";
import { persistLinkState } from "../hooks/useLinkTracker";
import { captureException } from "../sentry";
import Store from "../stateStore";
import { extractLinkParamsFromState, getBackendUrl, STORES } from "../utils";
import Sage50Hosted, { isHostedSage50Platform } from "./Sage50Hosted";

const { Step } = Steps;
const { Paragraph, Text } = Typography;

type Props = {
  clientSecret: string;
  onBack: () => any;
  onCancel: () => any;
  onComplete: (public_token: string) => any;
};

type SetupSession = {
  itemId: string;
  installerUrl: string;
  setupUri: string;
  expiresAt: string;
};

type ConnectorRelease = {
  version?: string;
  msi_url?: string;
};

const SAGE_50_SETUP_STORAGE_KEY = "rutterSage50Setup";

type StoredSetup = {
  orgId: string;
  itemId: string;
};

function Sage50Connector(props: Props) {
  const { onComplete } = props;
  const store = Store.useStore();
  const autoPlatform = store.get("autoPlatform");
  const orgId = store.get("orgId");
  const existingItemId = store.get("itemId");
  const [resumedItemId] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(SAGE_50_SETUP_STORAGE_KEY);
      if (!stored) return "";
      const parsed = JSON.parse(stored) as StoredSetup;
      return parsed.orgId === orgId ? parsed.itemId : "";
    } catch {
      return "";
    }
  });
  const [setup, setSetup] = useState<SetupSession | null>(null);
  const [stepNumber, setStepNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const activeItemId = setup?.itemId || existingItemId || resumedItemId;

  const handleAuthError = useCallback((e: any) => {
    captureException(e);
    setError(
      e.response?.data?.reason ??
        e.response?.data?.error_message ??
        UNKNOWN_ERROR_MESSAGE,
    );
  }, []);

  const startSetup = useCallback(async () => {
    setLoading(true);
    setError("");
    persistLinkState(STORES.SAGE_50, store.getState());
    try {
      const { data } = await axios.post(
        getBackendUrl() + "/sage-50/setup-session",
        {
          ...extractLinkParamsFromState(store),
          link_update_id: activeItemId || undefined,
        },
      );
      if (!data.is_successful) {
        setError(data.reason ?? UNKNOWN_ERROR_MESSAGE);
        return;
      }
      setSetup({
        itemId: data.item_id,
        installerUrl: data.installer_url,
        setupUri: data.setup_uri,
        expiresAt: data.expires_at,
      });
      store.set("itemId")(data.item_id);
      window.sessionStorage.setItem(
        SAGE_50_SETUP_STORAGE_KEY,
        JSON.stringify({ orgId, itemId: data.item_id } as StoredSetup),
      );
    } catch (e) {
      handleAuthError(e);
    } finally {
      setLoading(false);
    }
  }, [activeItemId, handleAuthError, orgId, store]);

  const verifyConnection = useCallback(async () => {
    if (!activeItemId) return;
    try {
      const { data } = await axios.post(
        getBackendUrl() + "/sage-50/link-verify",
        {
          ...extractLinkParamsFromState(store),
          link_update_id: activeItemId,
        },
      );
      if (data.is_successful) {
        window.sessionStorage.removeItem(SAGE_50_SETUP_STORAGE_KEY);
        onComplete(data.public_token);
      }
    } catch (e) {
      handleAuthError(e);
    }
  }, [activeItemId, handleAuthError, onComplete, store]);

  // Only poll after this visit has a setup session (or a reload of one).
  // /connections/:id of a healthy item must not auto-complete, or reauth
  // (re-approve after an EXE upgrade, pick the company again) is skipped.
  const shouldPollVerify = stepNumber >= 2 && (!!setup || !!resumedItemId);

  useEffect(() => {
    if (!activeItemId || !shouldPollVerify) return;
    verifyConnection();
    const interval = window.setInterval(verifyConnection, 5000);
    return () => window.clearInterval(interval);
  }, [activeItemId, shouldPollVerify, verifyConnection]);

  useEffect(() => {
    if (setup || !resumedItemId) return;
    axios
      .get(
        getBackendUrl() +
          `/sage-50/link-setup-info/organizations/${orgId}/items/${resumedItemId}`,
      )
      .then(({ data }) => {
        if (data.is_successful && data.company_name) setStepNumber(2);
      })
      .catch(handleAuthError);
  }, [handleAuthError, orgId, resumedItemId, setup]);

  const downloadInstaller = async () => {
    if (!setup) return;
    setLoading(true);
    setError("");
    try {
      // The legacy installer route redirects to one fixed S3 key, which browsers
      // may keep after that object is replaced. Prefer the immutable, versioned
      // MSI in the updater manifest and include its version in the cache key.
      let installerUrl = setup.installerUrl;
      try {
        const { data } = await axios.get<ConnectorRelease>(
          getBackendUrl() + "/sage-50/connector-release",
        );
        if (data.msi_url) {
          const releaseUrl = new URL(data.msi_url, window.location.href);
          if (data.version)
            releaseUrl.searchParams.set("version", data.version);
          installerUrl = releaseUrl.toString();
        }
      } catch (e) {
        captureException(e);
      }

      const link = document.createElement("a");
      link.href = installerUrl;
      link.download = "RutterSage50ConnectorSetup.msi";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setStepNumber(1);
    } finally {
      setLoading(false);
    }
  };

  const stepStatus = (step: number): "finish" | "process" | "wait" => {
    if (step < stepNumber) return "finish";
    return step === stepNumber ? "process" : "wait";
  };

  return (
    <Layout className="bg-white w-full">
      <Layout.Content className="overflow-y-auto px-16">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-[800px] py-8">
            {!autoPlatform && (
              <Button onClick={props.onBack} icon={<ArrowLeftOutlined />}>
                Back
              </Button>
            )}
            <div className="text-center mb-8">
              <img
                className="h-20 inline-block"
                src="https://superspeed-ph.com/wp-content/uploads/2018/09/sage50-new.png"
                alt="Sage 50"
              />
            </div>

            <Steps direction="vertical" current={stepNumber}>
              <Step
                title="Install the Sage 50 connector"
                status={stepStatus(0)}
                description={
                  <div className="space-y-3">
                    <Paragraph>
                      Rutter will create a one-time setup link. No Sage company
                      name or access token needs to be typed into the installer.
                    </Paragraph>
                    {!setup ? (
                      <Button
                        type="primary"
                        loading={loading}
                        onClick={startSetup}
                      >
                        Prepare Sage 50 setup
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        loading={loading}
                        style={{ display: "inline-flex", alignItems: "center" }}
                        icon={
                          <span className="anticon inline-flex items-center">
                            <DownloadOutlined />
                          </span>
                        }
                        onClick={downloadInstaller}
                      >
                        Download Sage 50 connector
                      </Button>
                    )}
                  </div>
                }
              />

              <Step
                title="Choose the company in the connector"
                status={stepStatus(1)}
                description={
                  setup ? (
                    <div>
                      <Paragraph>
                        After installation, open the connector below. It reads
                        the company list directly from Sage 50 and lets you pick
                        the exact company—there is nothing to type. If the
                        connector is already installed on this computer, skip
                        the download.
                      </Paragraph>
                      <Button
                        type="primary"
                        href={setup.setupUri}
                        style={{ display: "inline-flex", alignItems: "center" }}
                        icon={
                          <span className="anticon inline-flex items-center">
                            <ExportOutlined />
                          </span>
                        }
                        onClick={() => setStepNumber(2)}
                      >
                        Choose company in connector
                      </Button>
                      <Paragraph className="mt-2">
                        <Text type="secondary">
                          This setup link expires{" "}
                          {new Date(setup.expiresAt).toLocaleString()}.
                        </Text>
                      </Paragraph>
                    </div>
                  ) : (
                    <Text type="secondary">Prepare setup first.</Text>
                  )
                }
              />

              <Step
                title="Approve Sage access and finish the initial sync"
                status={stepStatus(2)}
                description={
                  <div>
                    <Paragraph>
                      Keep the connector running and complete both Sage prompts:
                      first, close and reopen the selected company as an
                      administrator and choose “Always Allow Access” for the
                      Rutter Sage 50 Connector. Then keep the company open; in
                      the “Peachtree Software” prompt, check “Remember this
                      setting” and click “Yes” to include General Ledger
                      transactions. If this second prompt is unavailable or
                      denied, the rest of the Sage data still syncs and General
                      Ledger transactions can be enabled later.
                    </Paragraph>
                    <Button
                      type="primary"
                      loading={loading}
                      disabled={stepNumber < 2}
                      onClick={async () => {
                        setLoading(true);
                        await verifyConnection();
                        setLoading(false);
                      }}
                    >
                      Verify connection
                    </Button>
                    <Paragraph className="mt-2">
                      This page checks automatically every five seconds.
                    </Paragraph>
                  </div>
                }
              />
            </Steps>

            {error && (
              <Alert
                className="mt-4"
                type="error"
                message="Unable to connect Sage 50"
                description={error}
                showIcon
              />
            )}
          </div>
        </div>
      </Layout.Content>
    </Layout>
  );
}

function Sage50(props: Props) {
  const store = Store.useStore();
  if (isHostedSage50Platform(store.get("autoPlatform"))) {
    return <Sage50Hosted {...props} />;
  }
  return <Sage50Connector {...props} />;
}

export default Sage50;
